import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import '../Dashboard/Dashboard.css';

interface User {
  id: number;
  email: string;
  confirmed_at: string | null;
  approved: boolean;
  is_super_admin: boolean;
}

interface ApiKey {
  id: number;
  name: string;
  token: string;
  created_at: string;
}

interface Props {
  users: User[];
  apiKeys: ApiKey[];
  currentUserId: number;
}

export default function SuperAdminIndex({ users, apiKeys, currentUserId }: Props) {
  const [newKeyName, setNewKeyName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState<Record<number, boolean>>({});

  const handleCheckboxChange = (user: User, newValue: boolean) => {
    if (currentUserId === user.id) return;
    setPendingApprovals(prev => {
      if (newValue === user.approved) {
        const next = { ...prev };
        delete next[user.id];
        return next;
      }
      return { ...prev, [user.id]: newValue };
    });
  };

  const saveApproval = (user: User) => {
    const newStatus = pendingApprovals[user.id];
    if (newStatus === undefined) return;
    router.put(`/super_admin/users/${user.id}`, { approved: newStatus }, {
      preserveScroll: true,
      onSuccess: () => {
        setPendingApprovals(prev => {
          const next = { ...prev };
          delete next[user.id];
          return next;
        });
      },
      onError: e => alert(JSON.stringify(e))
    });
  };

  const cancelApproval = (user: User) => {
    setPendingApprovals(prev => {
      const next = { ...prev };
      delete next[user.id];
      return next;
    });
  };

  const createApiKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyName.trim() || generating) return;
    setGenerating(true);
    router.post('/super_admin/api_keys', { name: newKeyName }, {
      preserveScroll: true,
      onSuccess: () => {
        setNewKeyName('');
        setGenerating(false);
      },
      onError: e => {
        alert("Error: " + JSON.stringify(e));
        setGenerating(false);
      },
      onFinish: () => setGenerating(false)
    });
  };

  const revokeApiKey = (id: number) => {
    if (confirm("Are you sure you want to revoke this API Key?")) {
      router.delete(`/super_admin/api_keys/${id}`, { preserveScroll: true });
    }
  };

  return (
    <div className="dashboard-root">
      <div className="sidebar" style={{ width: 260, backgroundColor: 'var(--bg-card)', borderRight: '1px solid var(--border)', padding: 24, display: 'flex', flexDirection: 'column' }}>
        <h2 style={{ marginBottom: 32 }}>SignalHouse</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <button style={{ textAlign: 'left', background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', padding: 0 }} onClick={() => router.get('/blasts')}>← Back to Main</button>
          <div style={{ color: 'var(--accent-2)', fontWeight: 'bold' }}>Super Admin</div>
        </div>
      </div>

      <div className="main-content" style={{ padding: 40, flex: 1, overflowY: 'auto' }}>
        <h1 style={{ marginBottom: 40 }}>Super Admin Panel</h1>

        <div style={{ display: 'grid', gap: 40 }}>
          <div className="card" style={{ padding: 24, backgroundColor: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>
            <h2 style={{ marginBottom: 20 }}>User Approvals</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>Email</th>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>Confirmed?</th>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>Super Admin?</th>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)', textAlign: 'center' }}>Approved</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isCurrentUser = currentUserId === u.id;
                  const isModified = pendingApprovals[u.id] !== undefined;
                  const currentVal = isModified ? pendingApprovals[u.id] : u.approved;
                  return (
                  <tr key={u.id} style={{ opacity: isCurrentUser ? 0.4 : 1, pointerEvents: isCurrentUser ? 'none' : 'auto' }}>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>{u.email} {isCurrentUser && <span style={{fontSize: '0.8rem', color: 'var(--text-3)', marginLeft: 8}}>(You)</span>}</td>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>{u.confirmed_at ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>{u.is_super_admin ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                        <label style={{ cursor: isCurrentUser ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                          <input type="checkbox" checked={currentVal} onChange={(e) => handleCheckboxChange(u, e.target.checked)} disabled={isCurrentUser} style={{ width: 18, height: 18, accentColor: 'var(--accent-1)' }} />
                        </label>
                        {isModified && (
                          <>
                            <button onClick={() => saveApproval(u)} style={{ backgroundColor: 'var(--accent-1)', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>Save</button>
                            <button onClick={() => cancelApproval(u)} style={{ backgroundColor: 'transparent', color: 'var(--text-2)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 11px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}>Cancel</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ padding: 24, backgroundColor: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)' }}>
            <h2 style={{ marginBottom: 20 }}>API Keys</h2>
            <form onSubmit={createApiKey} style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
              <input type="text" required value={newKeyName} onChange={e => setNewKeyName(e.target.value)} placeholder="New Key Name..." style={{ flex: 1, padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border)', backgroundColor: 'var(--bg-input)', color: '#fff' }} />
              <button disabled={!newKeyName.trim() || generating} type="submit" style={{ backgroundColor: 'var(--accent-1)', color: '#fff', padding: '10px 24px', borderRadius: 8, border: 'none', fontWeight: 600, cursor: (!newKeyName.trim() || generating) ? 'not-allowed' : 'pointer', opacity: (!newKeyName.trim() || generating) ? 0.6 : 1 }}>
                {generating ? 'Generating...' : 'Generate Key'}
              </button>
            </form>

            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>Name</th>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>Token</th>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>Created</th>
                  <th style={{ paddingBottom: 16, borderBottom: '1px solid var(--border)', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(k => (
                  <tr key={k.id}>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)', fontWeight: 500 }}>{k.name}</td>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)', fontFamily: 'monospace', color: 'var(--text-2)' }}>{k.token}</td>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>{new Date(k.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '16px 0', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                      <button onClick={() => revokeApiKey(k.id)} style={{ padding: '6px 16px', backgroundColor: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}>Revoke</button>
                    </td>
                  </tr>
                ))}
                {apiKeys.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-3)' }}>No API keys have been generated yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
